import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Chat, Message, MessageAttachment, MessageSearchResult } from '../models/message.model';

@Injectable({
  providedIn: 'root',
})
export class ConversationService {
  private readonly API_URL = `/api`;

  constructor(private http: HttpClient) {}

  getConversations(userId: number): Observable<Chat[]> {
    const url = `${this.API_URL}/conversations/${userId}`;

    return this.http.get<Chat[]>(`${this.API_URL}/conversations/${userId}`);
  }

  getMessages(conversationId: number, userId: number): Observable<Message[]> {
    const url = `${this.API_URL}/messages/${conversationId}/${userId}`;
    return this.http.get<Message[]>(url);
  }

  downloadTranscript(conversationId: number): Observable<Blob> {
    return this.http.get(`${this.API_URL}/messages/${conversationId}/transcript`, {
      responseType: 'blob',
    });
  }

  searchMessages(userId: number, query: string): Observable<MessageSearchResult[]> {
    return this.http.get<MessageSearchResult[]>(`${this.API_URL}/messages/search/${userId}`, {
      params: { q: query },
    });
  }

  markConversationRead(conversationId: number, currentUserId: number) {
    return this.http.post(`${this.API_URL}/messages/${conversationId}/read`, {
      currentUserId,
    });
  }

  sendMessage(payload: {
    conversation_id: number;
    sender_id: number;
    content: string;
    attachments?: MessageAttachment[];
    reply_to_message_id?: number;
  }) {
    return this.http.post<Message>(`${this.API_URL}/messages`, payload);
  }

  /**
   * Uploads files selected in the composer and returns their metadata —
   * does not attach them to any message. The caller bundles the returned
   * metadata into a subsequent sendMessage() call.
   */
  uploadMessageAttachments(files: File[]): Observable<MessageAttachment[]> {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));

    return this.http.post<MessageAttachment[]>(`${this.API_URL}/messages/attachments`, formData);
  }

  createDirectConversation(currentUserId: number, otherUserId: number) {
    return this.http.post<{
      conversationId: number;
      alreadyExists: boolean;
    }>(`${this.API_URL}/conversations/direct`, { currentUserId, otherUserId });
  }

  createGroupConversation(currentUserId: number, memberIds: number[], name: string) {
    const payload = { currentUserId, memberIds, name };

    return this.http.post(`${this.API_URL}/conversations/group`, {
      currentUserId,
      memberIds,
      name,
    });
  }

  addGroupMembers(conversationId: number, userIds: number[], currentUserId: number) {
    return this.http.post(`${this.API_URL}/conversations/${conversationId}/members`, {
      userIds,
      currentUserId,
    });
  }

  leaveConversation(conversationId: number, currentUserId: number) {
    return this.http.patch(`${this.API_URL}/conversations/${conversationId}/leave`, {
      currentUserId,
    });
  }

  setConversationMuted(conversationId: number, currentUserId: number, muted: boolean) {
    return this.http.patch<{ success: boolean; muted: boolean }>(
      `${this.API_URL}/conversations/${conversationId}/mute`,
      { currentUserId, muted },
    );
  }

  renameConversation(conversationId: number, name: string, currentUserId: number) {
    return this.http.patch<{ success: boolean; name: string }>(
      `${this.API_URL}/conversations/${conversationId}/name`,
      { name, currentUserId },
    );
  }
}
